// The RE-DRIVE: the one exit from `escalated` (terminal in
// `RAW_TASK_STATUS_TRANSITIONS`), fenced to escalations that were never a decision
// about the ask but a MACHINE failing to make one. Two doors, one fence:
//
//   - the MACHINE re-drive (2026-10-22 owner ruling: the AI makes every call and no
//     human works the escalated queue) — automatic, bounded by a budget and an
//     exponential backoff;
//   - the OPERATOR re-drive (#5113) — an explicit act, bounded by neither.
//
// `redriveRawTask` (`@developerz/db`) is the one writer for both, and restates
// {@link rawTaskRedriveVerdict} in its UPDATE's WHERE.

import { TriageVerdict } from '../../value-objects/triage-verdict';
import type { RawTask } from './row';
import { RawTaskStatus } from './status';
import { RAW_TASK_JUDGE_UNAVAILABLE_REASON } from './verdict';

/**
 * The shedder's reason (`apps/worker` `raw-task-bridge.ts`): the account's queue was past
 * its backlog horizon when the ask arrived. STORED twice: as an `escalate` reason on the
 * rows shed before 2026-10-22 (180 on prod that day), and as a `raw_tasks.hold_reason`
 * from then on — the shedder HOLDS (`RAW_TASK_INTAKE_SATURATED_HOLD_REASON`,
 * `@developerz/db`) instead of escalating. One spelling for both, here, because the
 * domain imports nothing and both the db and the worker read it.
 */
export const RAW_TASK_INTAKE_SATURATED_REASON = 'intake_saturated';

/**
 * When the trust line went live in prod (#7799): the first `developer_author` screen skip,
 * measured 2026-11-02 (`audit_events`, `output ->> 'rule'`). The intake screen read developers'
 * text until then, so an `injection` discard settled before it may be false
 * (`apps/worker/src/cron/ask-close-loop-injection.ts`). A fact, frozen: never moved by a roll.
 */
export const RAW_TASK_TRUST_LINE_LIVE_AT = new Date('2026-11-02T05:15:39.143Z');

/**
 * The triage judge could not establish whether live work already covers the ask, and
 * that was the ONE thing stopping it (`@developerz/agent` triage: an `escalate` whose
 * `escalate_cause` grades to coverage). A gap in the EVIDENCE the machine read, not a
 * decision about the ask: measured 2026-11-01, 85 of 596 settled asks in 72 h escalated
 * this way against a repo board truncated by dead rows, and none was ever re-judged. The
 * re-drive re-reads the board; the budget below bounds an ask whose coverage stays unknown.
 *
 * TWO WRITERS, ONE VALUE. The judge's `escalate_cause` grades onto it
 * (`escalateRecordReason`, `@developerz/agent` `triage/reason.ts`), and data-only
 * migration `0405` (`packages/db/migrations/0405_raw_tasks_coverage_unknown_reason.sql`)
 * re-spelled the rows already settled the old way onto the same reason — so the
 * stored value has one spelling whichever door wrote it.
 */
export const RAW_TASK_COVERAGE_UNKNOWN_REASON = 'coverage_unknown';

/**
 * The `escalate` reasons that are a MACHINE failing to decide, never a decision —
 * each re-drivable. Stored vocabulary; the writers and why each is not a decision:
 *
 * | reason | writer | why it is the machine's |
 * |---|---|---|
 * | `judge_unavailable` | none since #5110 (now a `model_unavailable` hold) | the judge could not answer |
 * | `attempts_exhausted` | `apps/agents-manager` `raw-task-triage.ts` at `RAW_TASK_MAX_ATTEMPTS` | no claim got a judgement |
 * | `claim_missing` | `@developerz/agent` triage, after the corrective re-ask (claim-failover now holds first) | the model's answer was malformed |
 * | `intake_saturated` | the shedder until 2026-10-22 (now a hold) | the queue was full |
 * | `coverage_unknown` | `@developerz/agent` triage, a judged `escalate` with `escalate_cause: coverage` | the evidence could not show whether work covers it |
 *
 * THIS list is the source: `@developerz/agent`'s writers are `satisfies RawTaskMachineFailureReason`, so a drift is a type error there. A reason missing here is merely never re-driven; one added
 * here that IS a decision would re-judge a person's call, so the list is closed and
 * every addition names its writer in the table above.
 */
export const RAW_TASK_MACHINE_FAILURE_REASONS = [
  RAW_TASK_JUDGE_UNAVAILABLE_REASON,
  'attempts_exhausted',
  'claim_missing',
  RAW_TASK_INTAKE_SATURATED_REASON,
  RAW_TASK_COVERAGE_UNKNOWN_REASON,
] as const;
export type RawTaskMachineFailureReason = (typeof RAW_TASK_MACHINE_FAILURE_REASONS)[number];

/**
 * How many MACHINE re-drives one ask gets over its whole life (`raw_tasks.machine_redrives`).
 * The named failure: an ask whose text reliably breaks the machine (a prompt that times
 * the judge out, an ask no model answers in shape) would otherwise re-run triage on the
 * CUSTOMER's key forever — every re-drive is a fresh `RAW_TASK_MAX_ATTEMPTS` ladder of
 * judge calls. Three re-drives on the 1h·4^n backoff span ~21h of retrying, past any
 * provider outage measured to date; after it the row stays `escalated` and the worker
 * alerts. Not a cost cap (`docs/standards/agent-budgets.md`): the bound is the loop.
 */
export const RAW_TASK_MACHINE_REDRIVE_BUDGET = 3;

/** The first backoff step; the n-th re-drive waits `base · 4^n` after the settle. */
export const RAW_TASK_MACHINE_REDRIVE_BACKOFF_BASE_MS = 60 * 60_000;

/** Each backoff step is this many times the last (`1h, 4h, 16h`). */
export const RAW_TASK_MACHINE_REDRIVE_BACKOFF_FACTOR = 4;

/**
 * When an ask settled at `settledAt`, already machine re-driven `machineRedrives` times,
 * may be re-driven again. `redriveRawTask` and `listMachineRedrivable` restate it in SQL
 * as `settled_at <= now() - base · factor ^ machine_redrives`.
 */
export function rawTaskMachineRedriveEligibleAt(settledAt: Date, machineRedrives: number): Date {
  return new Date(
    settledAt.getTime() +
      RAW_TASK_MACHINE_REDRIVE_BACKOFF_BASE_MS *
        RAW_TASK_MACHINE_REDRIVE_BACKOFF_FACTOR ** machineRedrives,
  );
}

/**
 * The OPERATOR re-drive (#5113), named so the exception to `escalated` being terminal is
 * one readable constant: `escalated -> pending` over the machine-failure reasons. NOT a
 * lifecycle move — `RAW_TASK_STATUS_TRANSITIONS` keeps `escalated: []`.
 */
export const RAW_TASK_OPERATOR_REDRIVE = {
  from: RawTaskStatus.Escalated,
  to: RawTaskStatus.Pending,
  reasons: RAW_TASK_MACHINE_FAILURE_REASONS,
} as const;

/** What the fence needs off the row: `machineRedrives` is `raw_tasks.machine_redrives`. */
export type RawTaskRedriveCandidate = Pick<RawTask, 'status' | 'verdict' | 'settledAt'> & {
  machineRedrives: number;
};

/**
 * The re-drive fence's answer, discriminated so the writer and the worker can say WHY:
 *
 *   - `redrivable` — escalated on a machine failure, under budget, past its backoff;
 *   - `not_machine_failure` — any other status, or an escalation that IS a decision
 *     (model prose, `info_*`, a permanent gate). Both doors refuse it;
 *   - `budget_spent` — {@link RAW_TASK_MACHINE_REDRIVE_BUDGET} used up. Machine door only;
 *   - `backing_off` — inside the backoff, eligible at `eligibleAt`. Machine door only.
 */
export type RawTaskRedriveVerdict =
  | { kind: 'redrivable' }
  | { kind: 'not_machine_failure' }
  | { kind: 'budget_spent'; machineRedrives: number }
  | { kind: 'backing_off'; eligibleAt: Date };

/** Is this an `escalated` row whose escalation a MACHINE wrote (shape only, no budget)? */
function isMachineFailureEscalation(task: RawTaskRedriveCandidate): boolean {
  return (
    task.status === RawTaskStatus.Escalated &&
    task.verdict !== null &&
    task.verdict.verdict === TriageVerdict.Escalate &&
    (RAW_TASK_MACHINE_FAILURE_REASONS as readonly string[]).includes(task.verdict.reason)
  );
}

/** Pure. The one fence: shape, then budget, then backoff (`now` is the caller's clock). */
export function rawTaskRedriveVerdict(
  task: RawTaskRedriveCandidate,
  now: Date,
): RawTaskRedriveVerdict {
  if (!isMachineFailureEscalation(task) || task.settledAt === null) {
    return { kind: 'not_machine_failure' };
  }
  if (task.machineRedrives >= RAW_TASK_MACHINE_REDRIVE_BUDGET) {
    return { kind: 'budget_spent', machineRedrives: task.machineRedrives };
  }
  const eligibleAt = rawTaskMachineRedriveEligibleAt(task.settledAt, task.machineRedrives);
  if (now.getTime() < eligibleAt.getTime()) return { kind: 'backing_off', eligibleAt };
  return { kind: 'redrivable' };
}

/** May the MACHINE re-drive this ask now? {@link rawTaskRedriveVerdict} is `redrivable`. */
export function isRawTaskMachineRedrivable(task: RawTaskRedriveCandidate, now: Date): boolean {
  return rawTaskRedriveVerdict(task, now).kind === 'redrivable';
}

/**
 * How far apart one account's bulk backlog walks start: the weekly tick and a posture change. One
 * walk files at most the drain's horizon, so one per drain window never sheds. THE FAILURE IT
 * PREVENTS, prod 2026-11-02 04:17: 49 walks at once filed 174 asks in 28 s and drew 250
 * `intake_saturated` holds in the next 4 h. `backlog-reimport.test.ts` holds it at or above
 * `RAW_TASK_DRAIN_WINDOW_MS` (2,777,080 ms), which only the worker can read. TRIP CONDITION: one
 * account's Monday outlasting the week (past ~200 enabled repos), or `intake_saturated` holds after
 * a tick. Prod's largest account runs 49 walks, about 38 h.
 */
export const BACKLOG_WALK_STAGGER_MS = 47 * 60_000;
